import { API_ORIGIN, apiUrl } from "./apiOrigin";
// PatientFeedbackPublic.js
// =============================================================================
// The page a pharmacy's "Feedback" poster QR code opens: "/?pfb=TOKEN".
// No login, no nav rail — same shape as SinglePharmacy's "?spview=TOKEN" and
// CqcContributorTask's "?cqcTask=TOKEN" (see those components' header
// comments, and App.js, for the early-return wiring this mirrors).
//
// Deliberately self-contained and independent of PatientFeedback.js (the
// manager-side module) — it duplicates the small bit of rendering logic
// rather than importing it, so this public, unauthenticated page has no
// dependency on anything that talks to a manager's token.
// =============================================================================

import React, { useEffect, useState } from "react";
import FeedbackContactLayout from "./FeedbackContactLayout";
const API_BASE = apiUrl("");
const S = {
  page: {
    minHeight: "100vh",
    background: "#F5F5F5",
    fontFamily: "system-ui, sans-serif",
    padding: "32px 16px",
    display: "flex",
    justifyContent: "center"
  },
  card: {
    background: "#fff",
    borderRadius: 12,
    width: 460,
    maxWidth: "100%",
    padding: 28,
    boxShadow: "0 4px 24px rgba(0,0,0,0.08)"
  },
  label: {
    fontSize: 12.5,
    fontWeight: 700,
    color: "#5D5D5D",
    display: "block",
    marginBottom: 6
  },
  input: {
    width: "100%",
    boxSizing: "border-box",
    padding: "9px 11px",
    fontSize: 13.5,
    border: "1px solid #D9D9D9",
    borderRadius: 6,
    fontFamily: "inherit",
    marginBottom: 14
  },
  textarea: {
    width: "100%",
    boxSizing: "border-box",
    padding: "9px 11px",
    fontSize: 13.5,
    minHeight: 100,
    border: "1px solid #D9D9D9",
    borderRadius: 6,
    fontFamily: "inherit",
    resize: "vertical",
    marginBottom: 14
  },
  btn: {
    width: "100%",
    padding: "12px 0",
    fontSize: 14.5,
    fontWeight: 700,
    border: "none",
    borderRadius: 8,
    background: "#722ED1",
    color: "#fff",
    cursor: "pointer"
  },
  ok: {
    fontSize: 14,
    color: "#389E0D",
    background: "#F6FFED",
    border: "1px solid #B7EB8F",
    borderRadius: 8,
    padding: "14px 16px",
    marginTop: 16,
    textAlign: "center"
  },
  err: {
    fontSize: 13,
    color: "#A8071A",
    background: "#FFF1F0",
    border: "1px solid #FFCCC7",
    borderRadius: 6,
    padding: "9px 12px",
    marginTop: 12
  }
};
function StarRating({
  value,
  onChange
}) {
  return <div style={{
    display: "flex",
    gap: 6,
    justifyContent: "center",
    marginBottom: 4
  }}>
      {[1, 2, 3, 4, 5].map(n => <span key={n} onClick={() => onChange(n)} style={{
      fontSize: 34,
      cursor: "pointer",
      color: n <= value ? "#FA8C16" : "#D9D9D9",
      lineHeight: 1
    }}>
          ★
        </span>)}
    </div>;
}
export default function PatientFeedbackPublic({
  token
}) {
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [site, setSite] = useState(null); // { pharmacyName, webpage }

  const [rating, setRating] = useState(0);
  const [comments, setComments] = useState("");
  const [isComplaint, setIsComplaint] = useState(false);
  const [customerName, setCustomerName] = useState("");
  const [customerEmail, setCustomerEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [trustpilotUrl, setTrustpilotUrl] = useState("");
  const [trustpilotError, setTrustpilotError] = useState("");
  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/api/feedback/public/site?token=${encodeURIComponent(token)}`);
        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(data.error || "This link isn't valid.");
        setSite(data);
      } catch (e) {
        setLoadError(e.message);
      }
      setLoading(false);
    })();
  }, [token]);
  const submit = async () => {
    setSubmitError(null);
    if (isComplaint) {
      if (!customerName.trim()) {
        setSubmitError("Please add your name — it's required for a complaint.");
        return;
      }
      if (!customerEmail.trim()) {
        setSubmitError("Please add your email — it's required for a complaint so we can respond.");
        return;
      }
    }
    if (site && site.webpage && site.webpage.showTrustpilotInvite) {
      if (!customerName.trim()) {
        setSubmitError("Please add your name so we can create your personal Trustpilot review invitation.");
        return;
      }
      if (!customerEmail.trim()) {
        setSubmitError("Please add your email so we can create your personal Trustpilot review invitation.");
        return;
      }
    }
    setSubmitting(true);
    try {
      const res = await fetch(`${API_BASE}/api/feedback/public/submit`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          token,
          rating: rating || undefined,
          comments,
          isComplaint,
          customerName,
          customerEmail
        })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || "Something went wrong sending your feedback.");
      setTrustpilotUrl(data.trustpilotUrl || "");
      setTrustpilotError(data.trustpilotError || "");
      setSubmitted(true);
    } catch (e) {
      setSubmitError(e.message);
    }
    setSubmitting(false);
  };
  if (loading) {
    return <div style={S.page}><div style={S.card}>Loading…</div></div>;
  }
  if (loadError || !site) {
    return <div style={S.page}>
        <div style={S.card}>
          <div style={{
          fontWeight: 700,
          fontSize: 16,
          marginBottom: 6
        }}>This link isn't valid</div>
          <div style={{
          fontSize: 13.5,
          color: "#5D5D5D"
        }}>{loadError || "Please ask the pharmacy for a fresh link."}</div>
        </div>
      </div>;
  }
  const {
    webpage,
    pharmacyName
  } = site;
  const align = webpage.pharmacyName && webpage.pharmacyName.align || "center";
  return <div style={S.page}>
      <div style={S.card}>
        <FeedbackContactLayout webpage={webpage} details={site.contactDetails} pharmacyName={pharmacyName}>
        {!webpage.pharmacyName?.positioned && <div style={{
          textAlign: align,
          fontSize: webpage.pharmacyName && webpage.pharmacyName.fontSize || 22,
          color: webpage.pharmacyName && webpage.pharmacyName.fg || "#111111",
          fontWeight: 700,
          marginBottom: 14
        }}>
          {pharmacyName || "Pharmacy feedback"}
        </div>}
        <div style={{
          fontSize: 15,
          color: "#434343",
          marginBottom: 22,
          textAlign: "center"
        }}>
          {webpage.introText || "Tell us about your visit"}
        </div>

        {submitted ? <>
            <div style={S.ok}>Thank you — your feedback has been sent.</div>
            {webpage.showTrustpilotInvite && trustpilotUrl && <div style={{
            marginTop: 18,
            textAlign: "center"
          }}>
                <div style={{
              fontSize: 14,
              color: "#434343",
              marginBottom: 10
            }}>
                  {webpage.trustpilotInviteText || "Would you also like to share your experience on Trustpilot?"}
                </div>
                <a href={trustpilotUrl} target="_blank" rel="noopener noreferrer" style={{
              ...S.btn,
              display: "block",
              boxSizing: "border-box",
              textDecoration: "none"
            }}>
                  Review us on Trustpilot
                </a>
              </div>}
            {trustpilotError && <div style={S.err}>{trustpilotError}</div>}
          </> : <>
            {webpage.showRating && <div style={{
            marginBottom: 20,
            textAlign: "center"
          }}>
                <div style={{
              fontSize: 12.5,
              fontWeight: 700,
              color: "#5D5D5D",
              marginBottom: 4,
              letterSpacing: 0.3
            }}>
                  HOW WAS YOUR VISIT?
                </div>
                {webpage.ratingHelp && <div style={{
              fontSize: 12,
              color: "#8C8C8C",
              marginBottom: 8
            }}>{webpage.ratingHelp}</div>}
                <StarRating value={rating} onChange={setRating} />
              </div>}

            {webpage.showComments && <>
                <label style={S.label}>Feedback or comments</label>
                {webpage.commentsHelp && <div style={{
              fontSize: 12,
              color: "#8C8C8C",
              marginTop: -8,
              marginBottom: 8
            }}>{webpage.commentsHelp}</div>}
                <textarea style={S.textarea} value={comments} onChange={e => setComments(e.target.value)} placeholder="Tell us what you think…" />
              </>}

            {webpage.showComplaint && <div style={{
            marginBottom: 16
          }}>
                <label style={{
              fontSize: 13.5,
              color: "#434343",
              display: "flex",
              alignItems: "center",
              gap: 8
            }}>
                  <input type="checkbox" checked={isComplaint} onChange={e => setIsComplaint(e.target.checked)} />
                  This is a complaint
                </label>
                {webpage.complaintHelp && <div style={{
              fontSize: 12,
              color: "#8C8C8C",
              marginTop: 4,
              marginLeft: 24
            }}>{webpage.complaintHelp}</div>}
              </div>}

            <label style={S.label}>
              Your name {isComplaint ? <span style={{
              color: "#CF1322"
            }}>(required for a complaint)</span> : webpage.showTrustpilotInvite ? <span style={{
              color: "#CF1322"
            }}>(required for a Trustpilot invitation)</span> : "(optional)"}
            </label>
            <input style={{
            ...S.input,
            ...(isComplaint && !customerName.trim() ? {
              borderColor: "#FFA39E"
            } : {})
          }} value={customerName} onChange={e => setCustomerName(e.target.value)} required={isComplaint} />

            <label style={S.label}>
              Your email {isComplaint ? <span style={{
              color: "#CF1322"
            }}>(required for a complaint, so we can respond)</span> : webpage.showTrustpilotInvite ? <span style={{
              color: "#CF1322"
            }}>(required for a Trustpilot invitation)</span> : "(optional — only needed if you'd like a reply)"}
            </label>
            <input style={{
            ...S.input,
            ...(isComplaint && !customerEmail.trim() ? {
              borderColor: "#FFA39E"
            } : {})
          }} type="email" value={customerEmail} onChange={e => setCustomerEmail(e.target.value)} required={isComplaint} />

            <button style={S.btn} onClick={submit} disabled={submitting}>{submitting ? "Sending…" : "Send"}</button>
            {submitError && <div style={S.err}>{submitError}</div>}
          </>}
        </FeedbackContactLayout>
      </div>
    </div>;
}
